import { Form } from '@webinex/antik';
import * as Yup from 'yup';
import { formatters } from '@app/ui';
import { EmployeeDetailsFieldSet, EmployeeEmailFieldConfig } from './EmployeeDetailsFieldSet';
import { PROVIDER_INFO_INITIAL_VALUE, PROVIDER_INFO_SCHEMA } from './ProviderInfoFieldSet';
import { OptionInitialValue, Role } from '@app/main/api';

const EMPLOYEE_SCHEMA = Yup.object({
  email: Yup.string().email().field().nullable().required(),
  firstName: Yup.string().field().nullable().required(),
  lastName: Yup.string().field().nullable().required(),
  phone: Yup.string().field().phone().nullable().defined(),
  roles: Yup.array().of(Yup.mixed<Role>().required()).min(1).required(),
  providerInfo: PROVIDER_INFO_SCHEMA.nullable().when('role', {
    is: (x: Role) => x === Role.PERFORMER,
    then: (schema) => schema.required(),
  }),
  careHomeId: Yup.string().field().nullable().defined().when('roles', {
    is: (x: Role) => x.includes(Role.CARE_HOME_STAFF),
    then: (schema) => schema.required(),
  }),
});

export const EMPLOYEE_INITIAL_VALUE: EmployeeDetailsFormValue = {
  email: null!,
  firstName: null!,
  lastName: null!,
  phone: null!,
  roles: [Role.PERFORMER],
  providerInfo: PROVIDER_INFO_INITIAL_VALUE,
  careHomeId: null!,
};

export type EmployeeDetailsFormValue = Yup.InferType<typeof EMPLOYEE_SCHEMA> & {
  specialty?: OptionInitialValue;
};

interface Props {
  value: EmployeeDetailsFormValue;
  onSubmit: (value: EmployeeDetailsFormValue) => any;
  email: EmployeeEmailFieldConfig;
  roles: 'readonly' | true;
  avatar: boolean;
  uid: string;
  i18nPrefix: string;
}

export function EmployeeDetailsForm(props: Props) {
  const { value, onSubmit, email, roles, avatar, uid, i18nPrefix: prefix } = props;
  return (
    <Form
      uid={uid}
      type="formik"
      ns={prefix}
      initialValues={value}
      validationSchema={EMPLOYEE_SCHEMA}
      onSubmit={onSubmit}
      rootClassName="EmployeeDetailsForm"
    >
      <Form.OnInvalidSubmit type="notify" />
      {avatar && <Form.UserAvatarEdit fullName={formatters.name(value)} name="avatar" />}
      <EmployeeDetailsFieldSet initialValue={value} email={email} roles={roles} />
    </Form>
  );
}
